import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Layers,
  MapPin,
  PencilLine,
  RotateCcw,
  Save,
  ShieldCheck,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';
import type { SoverdiSectorView } from '../adapters/agriSafeDashboardAdapter';
import {
  MAX_CUSTOM_NAME_LENGTH,
  canRenameSector,
  normalizeCustomDisplayName,
} from '../adapters/sectorNaming';

interface SectorNameRowProps {
  sector: SoverdiSectorView;
  editingEnabled: boolean;
  onRename: (siteName: string, sectorCode: string, name: string | null, bindingId?: string) => Promise<void>;
}

const SectorNameRow: React.FC<SectorNameRowProps> = ({ sector, editingEnabled, onRename }) => {
  useTranslation();
  const inputId = useId();
  const [draft, setDraft] = useState(sector.customDisplayName ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(sector.customDisplayName ?? '');
  }, [sector.id, sector.customDisplayName]);

  const canonicalName = sector.canonicalDisplayName || i18n.t("sectorViews.Nom_metier_indisponible");
  const normalizedDraft = normalizeCustomDisplayName(draft);
  const currentCustom = sector.customDisplayName ?? null;
  const unchanged = normalizedDraft === currentCustom;
  const tooLong = draft.trim().length > MAX_CUSTOM_NAME_LENGTH;

  const save = async () => {
    setMessage(null);
    setError(null);
    if (!editingEnabled) return;
    if (!normalizedDraft) {
      setError(i18n.t("sectorViews.Saisissez_un_nom_entre_1_et_MAX_CUSTOM_NAME_LENGTH_caracteres", { MAX_CUSTOM_NAME_LENGTH: MAX_CUSTOM_NAME_LENGTH }));
      return;
    }

    try {
      setSaving(true);
      if (sector.bindingId) await onRename(sector.siteName, sector.sectorCode, normalizedDraft, sector.bindingId);
      else await onRename(sector.siteName, sector.sectorCode, normalizedDraft);
      setDraft(normalizedDraft);
      setMessage(i18n.t("sectorViews.Nom_enregistre"));
    } catch {
      setError(i18n.t("sectorViews.Le_nom_n_a_pas_pu_etre_enregistre_Reessayez_ou_contactez_Agri_Safe_si_"));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setMessage(null);
    setError(null);
    if (!editingEnabled || !sector.customDisplayName) return;

    try {
      setSaving(true);
      if (sector.bindingId) await onRename(sector.siteName, sector.sectorCode, null, sector.bindingId);
      else await onRename(sector.siteName, sector.sectorCode, null);
      setDraft('');
      setMessage(i18n.t("sectorViews.Nom_d_origine_retabli"));
    } catch {
      setError(i18n.t("sectorViews.Le_nom_d_origine_n_a_pas_pu_etre_retabli_Reessayez_ou_contactez_Agri_S"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-[var(--border-light)] px-4 py-4 last:border-b-0 sm:px-5" data-testid="sector-name-row">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,1fr)] lg:items-start">
        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
              <Layers className="h-4 w-4 text-[var(--color-primary)]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Nom_d_origine")}</p>
              <p className="mt-1 font-semibold text-[var(--text-primary)]" data-testid="sector-canonical-name">{canonicalName}</p>
              {sector.customDisplayName && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  {i18n.t("sectorViews.Nom_affiche_actuellement")}<span className="font-medium text-[var(--text-primary)]">{sector.displayName}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]" htmlFor={inputId}>
            {i18n.t("customName")}</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id={inputId}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setMessage(null);
                setError(null);
              }}
              disabled={!editingEnabled || saving}
              maxLength={MAX_CUSTOM_NAME_LENGTH + 1}
              placeholder={canonicalName}
              aria-label={i18n.t("sectorViews.Nom_personnalise_pour_canonicalName", { canonicalName: canonicalName })}
              className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 disabled:cursor-not-allowed disabled:bg-[var(--gray-50)] disabled:text-[var(--text-tertiary)]"
            />
            <button
              type="button"
              onClick={save}
              disabled={!editingEnabled || saving || !normalizedDraft || unchanged || tooLong}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={i18n.t("sectorViews.saveNameFor", { canonicalName })}
            >
              <Save className="h-4 w-4" />
              {i18n.t("save")}</button>
          </div>

          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className={`text-xs ${tooLong ? 'text-red-700' : 'text-[var(--text-tertiary)]'}`}>
              {draft.trim().length}/{MAX_CUSTOM_NAME_LENGTH} {i18n.t("sectorViews.caracteres")}</p>
            {sector.customDisplayName && (
              <button
                type="button"
                onClick={reset}
                disabled={!editingEnabled || saving}
                className="inline-flex min-h-[36px] items-center gap-1.5 self-start rounded-lg px-2 text-xs font-semibold text-[var(--color-primary)] transition-colors hover:bg-[var(--gray-100)] disabled:cursor-not-allowed disabled:opacity-40 sm:self-auto"
                aria-label={i18n.t("sectorViews.Retablir_le_nom_d_origine_de_canonicalName", { canonicalName: canonicalName })}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {i18n.t("sectorViews.Retablir_le_nom_d_origine")}</button>
            )}
          </div>

          {error && (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" role="alert" data-testid="sector-name-error">
              {error}
            </div>
          )}
          {message && (
            <div className="mt-2 flex items-center gap-2 text-xs font-medium text-emerald-700" role="status" data-testid="sector-name-success">
              <CheckCircle2 className="h-4 w-4" />
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SoverdiSectorNamingR7: React.FC = () => {
  useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    topology,
    sectors: legacySectors,
    operationalSectors,
    bindingsUnavailable,
    loading,
    renameSector,
    namesUnavailable,
  } = useAgriSafeSoverdiDashboard();
  const sectors = operationalSectors ?? legacySectors;
  const [selectedSite, setSelectedSite] = useState('');

  const siteNames = useMemo(() => topology?.sites.map((site) => site.name) ?? [], [topology]);
  const effectiveSite = selectedSite || siteNames[0] || '';
  const visibleSectors = useMemo(
    () => sectors.filter((sector) => sector.siteName === effectiveSite),
    [sectors, effectiveSite],
  );
  const inventoryUnavailable = bindingsUnavailable && topology?.sites.some(site => site.name === 'Roubia');
  const siteUnavailable = bindingsUnavailable && effectiveSite === 'Roubia';
  const customCount = namesUnavailable || inventoryUnavailable ? null : sectors.filter((sector) => sector.customDisplayName !== null).length;
  const roleAllowsRename = canRenameSector(user?.role);
  const editingEnabled = roleAllowsRename && !namesUnavailable;

  if (loading) {
    return (
      <div className="min-h-[55vh] flex items-center justify-center" data-testid="sector-naming-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--color-primary)] border-t-transparent" />
      </div>
    );
  }

  if (!topology) {
    return (
      <div className="space-y-4" data-testid="sector-naming-load-error">
        <button
          type="button"
          onClick={() => navigate('/r6-soverdi-settings')}
          className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--gray-100)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {i18n.t("settings")}</button>
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">{i18n.t("sectorViews.Noms_des_secteurs_indisponibles")}</p>
            <p className="mt-1 text-sm">{i18n.t("sectorViews.La_topologie_de_l_exploitation_n_a_pas_pu_etre_chargee_Aucun_secteur_f")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="soverdi-sector-naming-r7">
      <section>
        <button
          type="button"
          onClick={() => navigate('/r6-soverdi-settings')}
          className="mb-3 inline-flex min-h-[40px] items-center gap-2 rounded-lg px-2 text-sm font-medium text-[var(--color-primary)] transition-colors hover:bg-[var(--gray-100)]"
          data-testid="sector-naming-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {i18n.t("settings")}</button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[var(--color-primary)]">
              <PencilLine className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">{i18n.t("exploitation")}</span>
            </div>
            <h2 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{i18n.t("sectorViews.Noms_des_secteurs")}</h2>
            <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
              {i18n.t("sectorViews.Personnalisez_uniquement_le_nom_affiche_dans_Agri_Safe_Le_nom_d_origin")}</p>
          </div>
          <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Metadonnee_metier")}</span>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Sites")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{topology.summary.siteCount}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Secteurs")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{inventoryUnavailable ? i18n.t("availabilityUnavailable") : sectors.length}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 lg:col-span-1">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Noms_personnalises")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="sector-custom-count">{customCount === null ? '—' : customCount}</p>
        </div>
      </section>

      {namesUnavailable && (
        <section className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900" role="status" data-testid="sector-names-unavailable">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">{i18n.t("sectorViews.Personnalisation_temporairement_indisponible")}</p>
            <p className="mt-1 text-sm">{i18n.t("sectorViews.Les_secteurs_restent_affiches_avec_leurs_noms_d_origine_mais_les_noms_")}</p>
          </div>
        </section>
      )}

      {!roleAllowsRename && (
        <section className="flex items-start gap-3 rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4" data-testid="sector-naming-readonly-role">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" />
          <div>
            <p className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Consultation_uniquement")}</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Votre_role_permet_de_consulter_les_noms_metier_mais_pas_de_les_modifie")}</p>
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] overflow-hidden">
        <div className="border-b border-[var(--border-light)] p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[var(--color-primary)]" />
            <h3 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Choisir_un_site")}</h3>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {siteNames.map((siteName) => (
              <button
                key={siteName}
                type="button"
                onClick={() => setSelectedSite(siteName)}
                className={`min-h-[40px] rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
                  siteName === effectiveSite
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                    : 'border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--gray-100)]'
                }`}
                aria-pressed={siteName === effectiveSite}
              >
                {siteName}
              </button>
            ))}
          </div>
        </div>

        <div className="border-b border-[var(--border-light)] bg-[var(--gray-50)] px-4 py-3 sm:px-5">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{effectiveSite}</p>
          <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{siteUnavailable ? i18n.t("availabilityUnavailable") : i18n.t("sectorViews.sectorCount", { count: visibleSectors.length })}</p>
        </div>

        {visibleSectors.length > 0 ? (
          visibleSectors.map((sector) => (
            <SectorNameRow
              key={sector.id}
              sector={sector}
              editingEnabled={editingEnabled && !(sector.bindingId && bindingsUnavailable)}
              onRename={renameSector}
            />
          ))
        ) : (
          <div className="px-4 py-8 text-center text-sm text-[var(--text-secondary)]">{siteUnavailable ? i18n.t("availabilityUnavailable") : i18n.t("sectorViews.Aucun_secteur_declare_pour_ce_site")}</div>
        )}
      </section>

      <section className="flex items-start gap-3 rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 sm:p-5">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" />
        <div>
          <p className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Identite_technique_protegee")}</p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Cette_page_modifie_uniquement_la_presentation_Les_identifiants_de_sect")}</p>
        </div>
      </section>
    </div>
  );
};

export default SoverdiSectorNamingR7;
